// Bomb the Hill's traffic lane obeying the lights (#214). The road's
// traffic drives down the hill slower than you, and at each side street it
// meets the lights: it goes on green if it's through before the side street
// goes green, keeps going on amber only if it's too close to stop, and
// otherwise eases to a stop at the stop line, queueing behind whatever
// stopped first, then pulls away on green. It doesn't go into a side street
// it would have to stand in, behind a queue for the next one (#206). A vehicle catching up with a
// slower one ahead stays behind it. Sim-free and three.js-free: `HillRoad`
// keeps one `TrafficFlow` a run and `trafficZ` asks it where each vehicle is.
//
// It's stepped at a fixed `TRAFFIC_DT` from when the run's traffic set off,
// only as far as it's asked, and remembers every step, so the same seed
// asked the same things is the same traffic.

import { JUNCTION, roadLight, TRAFFIC_DRIVERS, untilSideGreen, type HillJunction, type HillTraffic, type TrafficDriver } from "./hill-course";

/** The traffic lane's step (s). */
export const TRAFFIC_DT = 1 / 20;

/**
 * How the traffic lane drives (#214): how hard it pulls away and eases to a
 * stop (m/s²), the hardest it will brake for an amber light (any closer and
 * it goes on through), how far before a side street its stop line is (m),
 * and the gap it keeps to the vehicle ahead standing and at its closest.
 */
export const TRAFFIC = {
  accel: 2.5,
  brake: 2.5,
  hardBrake: 5,
  stopLine: 1.2,
  queue: 2,
  closest: 1,
} as const;

/** Where a side street's stop line is for the road's traffic (course z): the vehicle's front stops here. */
export function stopLineZ(j: HillJunction): number {
  return j.z - j.hl - TRAFFIC.stopLine;
}

/** A vehicle in the flow: where it is now, how fast, and where it was every step since it joined. */
interface Driven {
  v: HillTraffic;
  z: number;
  speed: number;
  /** The step it joined at, and its z every step from then. */
  from: number;
  track: number[];
  /** The side street (its index in `junctions`) whose stop line it's past, committed to going through on amber. */
  committed: number;
}

/** The road's traffic (#214) for one run: see the top of this file. */
export class TrafficFlow implements TrafficDriver {
  /** The side streets laid out so far, by z (course z). */
  private junctions: HillJunction[] = [];
  private cars = new Map<HillTraffic, Driven>();
  /** The ones dropped, as they were. */
  private gone = new WeakMap<HillTraffic, Driven>();
  /** Steps taken so far. */
  private n = 0;

  /** Side streets laid out further down: their lights apply from now on. */
  addJunctions(js: readonly HillJunction[]): void {
    if (!js.length) return;
    this.junctions.push(...js);
    this.junctions.sort((p, q) => p.z - q.z);
  }

  /**
   * A vehicle laid out further down joins at `t` seconds into the run's
   * traffic (or the step it's got to already, if further), where it would
   * be having driven on at its speed since the traffic set off (or, if
   * that's in a side street or on top of another, behind it).
   */
  add(v: HillTraffic, t = 0): void {
    this.extend(t);
    let z = v.start + v.speed * this.n * TRAFFIC_DT;
    // Never in a side street, nor on top of one that got there first (held up at a light, say): waiting at its stop line, or queued behind.
    for (let moved = true; moved; ) {
      moved = false;
      for (const j of this.junctions) {
        if (z + v.hl > stopLineZ(j) + 1e-6 && z - v.hl < j.z + j.hl + TRAFFIC.closest) {
          z = stopLineZ(j) - v.hl;
          moved = true;
        }
      }
      for (const o of this.cars.values()) {
        if (Math.abs(o.z - z) < o.v.hl + v.hl + TRAFFIC.queue - 1e-6) {
          z = o.z - o.v.hl - TRAFFIC.queue - v.hl;
          moved = true;
        }
      }
    }
    this.cars.set(v, { v, z, speed: v.speed, from: this.n, track: [z], committed: -1 });
    TRAFFIC_DRIVERS.set(v, this);
  }

  /** Stops driving a vehicle that's well behind: from then on it's taken to drive straight on from where it was. */
  drop(v: HillTraffic): void {
    const d = this.cars.get(v);
    if (!d) return;
    this.cars.delete(v);
    this.gone.set(v, d);
  }

  /** Where `v` is (course z) `t` seconds into the run's traffic. */
  z(v: HillTraffic, t: number): number {
    const gone = this.gone.get(v);
    const d = gone ?? this.cars.get(v);
    if (!d) return v.start + v.speed * t;
    if (gone) {
      const end = (d.from + d.track.length - 1) * TRAFFIC_DT;
      if (t > end) return d.track.at(-1)! + v.speed * (t - end);
    } else this.extend(t);
    const f = t / TRAFFIC_DT - d.from;
    if (f <= 0) return d.track[0]! + v.speed * f * TRAFFIC_DT;
    const i = Math.min(Math.floor(f), d.track.length - 1);
    const a = d.track[i]!;
    const b = d.track[Math.min(i + 1, d.track.length - 1)]!;
    return a + (b - a) * (f - i);
  }

  /** Steps on until `t` seconds into the run's traffic is covered. */
  private extend(t: number): void {
    while (this.n * TRAFFIC_DT < t) this.step();
  }

  private step(): void {
    const time = this.n * TRAFFIC_DT;
    const dt = TRAFFIC_DT;
    // Front to back, so each one knows where the one ahead has got to this step.
    const order = [...this.cars.values()].sort((p, q) => q.z - p.z);
    let ahead: Driven | null = null;
    for (const d of order) {
      const { v } = d;
      let top = Math.min(v.speed, d.speed + TRAFFIC.accel * dt);
      if (ahead) {
        // Slowing to stand `queue` behind it (it's moved this step already), never closer than `closest`.
        const gap = ahead.z - ahead.v.hl - (d.z + v.hl);
        top = Math.min(top, Math.sqrt(Math.max(0, ahead.speed * ahead.speed + 2 * TRAFFIC.brake * (gap - TRAFFIC.queue))));
        top = Math.min(top, Math.max(0, (gap - TRAFFIC.closest) / dt));
      }
      const stop = this.stopFor(d, time);
      if (stop !== null) {
        const room = stop - (d.z + v.hl);
        // Eases down to stand at the line: no faster than it can stop from at `brake`, braking harder only if it has to; never past it.
        const left = Math.max(1e-3, room - (d.speed * dt) / 2);
        const easy = Math.sqrt(2 * TRAFFIC.brake * left);
        top = Math.min(top, d.speed > easy ? Math.max(0, d.speed - ((d.speed * d.speed) / (2 * left)) * dt) : easy);
        top = Math.min(top, Math.max(0, room / dt));
      }
      d.speed = Math.max(0, top);
      d.z += d.speed * dt;
      d.track.push(d.z);
      ahead = d;
    }
    this.n++;
  }

  /**
   * Whether `d` would have room to stand clear past side street `i` (don't
   * block the box, #206): with everything ahead of it short of the next side
   * street's stop line queued there, its back is still past this one. Only
   * ever grows as it waits (no one gets in ahead), so it doesn't flicker.
   */
  private roomPast(d: Driven, i: number): boolean {
    const next = this.junctions[i + 1];
    if (!next) return true;
    const line = stopLineZ(next);
    let front = line;
    for (const o of this.cars.values()) if (o !== d && o.z > d.z && o.z + o.v.hl <= line + 1e-6) front -= 2 * o.v.hl + TRAFFIC.queue;
    const j = this.junctions[i]!;
    return front - 2 * d.v.hl >= j.z + j.hl;
  }

  /**
   * The stop line `d` must stand at by the end of this step (course z), if
   * the next side street's lights say stop: red; amber, unless it's too
   * close to stop and will be through in time; green, unless it'd still be
   * in the side street when the side street goes green.
   */
  private stopFor(d: Driven, time: number): number | null {
    const front = d.z + d.v.hl;
    const i = this.junctions.findIndex((j) => stopLineZ(j) > front - 1e-6);
    if (i < 0 || i === d.committed) return null;
    const j = this.junctions[i]!;
    const line = stopLineZ(j);
    const room = line - front;
    // Too far off to matter yet.
    if (room > (d.v.speed * d.v.speed) / (2 * TRAFFIC.brake) + d.v.speed * 2) return null;
    const light = roadLight(j.light, time);
    if (light === "red") return line;
    // Through, back clear of the side street, before its traffic goes, with room to stand past it.
    const through = clearTime(d.speed, d.v.speed, j.z + j.hl + d.v.hl - d.z) < untilSideGreen(j.light, time) - JUNCTION.react && this.roomPast(d, i);
    if (light === "green") return through ? null : line;
    // Amber: it stops if it can, and goes on through only if it's too close to stop and will be through in time.
    if (room < (d.speed * d.speed) / (2 * TRAFFIC.hardBrake) && through) {
      d.committed = i;
      return null;
    }
    return line;
  }
}

/** How long (s) from `u` m/s, pulling away at `TRAFFIC.accel` up to `v`, it takes to go `dist` metres. */
function clearTime(u: number, v: number, dist: number): number {
  if (dist <= 0) return 0;
  const a = TRAFFIC.accel;
  const up = (v * v - u * u) / (2 * a);
  if (dist <= up) return (-u + Math.sqrt(u * u + 2 * a * dist)) / a;
  return (v - u) / a + (dist - up) / v;
}
